import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Select } from '../ui/Input';
import ConfirmDialog from '../ui/ConfirmDialog';
import useConfirm from '../ui/useConfirm';
import EditorPDF from '../ListarPecas/EditorPDF';
import {
  createStepConfirmationFormData,
  createStepGeometryFormData,
  hasStepIdentity,
} from '../../utils/stepImport';
import '../ListarPecas/ListarPecas.css';

const localizarArquivoOriginal = (files, arquivoOrigem) => {
  const matches = (files || []).filter(file => file.name === arquivoOrigem);
  return matches.length === 1 ? matches[0] : null;
};

const confirmarProvenienciaStep = ({ file, pecaId, stepPeca }) => {
  return api.post(
    '/api/step/importacao/confirmar',
    createStepConfirmationFormData({ file, pecaId, stepPiece: stepPeca }),
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
};

const interpretarFalhaConfirmacao = (error, pecaId) => {
  const payload = error.response?.data;
  let message = 'Geometria salva, mas a proveniência STEP não foi confirmada.';

  if (payload?.code === 'STEP_IDENTITY_CONFLICT') {
    message += ' A identidade foi associada a outra peça durante a confirmação.';
    return {
      message,
      confirmationStatus: 'conflict',
      data: {
        estado: 'CONFLICT',
        ...payload.identidade,
        peca_id: payload.peca_considerada_id ?? Number(pecaId),
        peca_associada_id: payload.peca_associada_id,
      },
    };
  }

  if (payload?.code === 'STEP_FILE_HASH_MISMATCH') {
    message += ' Os bytes do arquivo não correspondem ao SHA-256 analisado.';
  } else if (typeof payload?.detail === 'string') {
    message += ` ${payload.detail}`;
  }
  return { message, confirmationStatus: 'pending', data: null };
};

function ProductOperations({ pecas, codigoProduto, productId = null, onPartsUpdated = () => {} }) {
  const navigate = useNavigate();
  const [showModalStep, setShowModalStep] = useState(false);
  const [stepFile, setStepFile] = useState(null);
  const [stepPecas, setStepPecas] = useState([]);
  const [atribuicoes, setAtribuicoes] = useState({});
  const [loadingStep, setLoadingStep] = useState(false);
  const [erroStep, setErroStep] = useState(null);
  const [previewsStep, setPreviewsStep] = useState({});
  const [etapaStep, setEtapaStep] = useState(null);
  const [sucessoStep, setSucessoStep] = useState(null);
  const [stepConcluido, setStepConcluido] = useState(false);
  const [gerandoPDFs, setGerandoPDFs] = useState(false);
  const [gerandoMPRs, setGerandoMPRs] = useState(false);
  const [statusProduto, setStatusProduto] = useState('CÓPIA CONTROLADA');
  const [showEditorPDF, setShowEditorPDF] = useState(false);
  const [pecasParaEditar, setPecasParaEditar] = useState([]);
  const [showModalDownload, setShowModalDownload] = useState(false);
  const [pendingPecas, setPendingPecas] = useState([]);
  const [pendingConfig, setPendingConfig] = useState({});
  const [confirmPDF, setConfirmPDF] = useState(null); // peças aguardando decisão de editar antes de gerar
  const [confirmEl, confirm] = useConfirm();
  const stepSubmitInFlight = useRef(false);
  const confirmationRetriesInFlight = useRef(new Set());

  useEffect(() => {
    const assignedIndexes = Object.entries(atribuicoes)
      .filter(([, pecaId]) => Boolean(pecaId))
      .map(([stepIdx]) => Number(stepIdx));
    if (assignedIndexes.length === 0) return;

    const allFinished = assignedIndexes.every((stepIdx) => {
      const preview = previewsStep[stepIdx];
      return preview?.geometrySaved
        && ['confirmed', 'conflict'].includes(preview.confirmationStatus);
    });
    if (allFinished) setStepConcluido(true);
  }, [atribuicoes, previewsStep]);

  // ===== FUNÇÕES DO MODAL STEP =====

  const handleStepFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    stepSubmitInFlight.current = false;
    confirmationRetriesInFlight.current.clear();
    setStepFile(files);
    setLoadingStep(true);
    setEtapaStep('parsing');
    setErroStep(null);
    setSucessoStep(null);
    setStepConcluido(false);
    setStepPecas([]);
    setAtribuicoes({});
    setPreviewsStep({});
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      const response = await api.post('/api/step/parse-batch', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { pecas: pecasStep, erros } = response.data;
      setStepPecas(pecasStep);
      if (erros && erros.length > 0) console.warn('Erros no parse:', erros);
      const atribInit = {};
      pecasStep.forEach((p, idx) => { atribInit[idx] = ''; });
      setAtribuicoes(atribInit);
    } catch (error) {
      setErroStep(error.response?.data?.detail || 'Erro ao processar arquivos STEP');
    } finally {
      setLoadingStep(false);
      setEtapaStep(null);
    }
  };

  const handleAtribuicaoChange = async (stepIdx, pecaId) => {
    if (
      atribuicoes[stepIdx] === pecaId
      && previewsStep[stepIdx]?.loading
    ) return;

    setAtribuicoes(prev => ({ ...prev, [stepIdx]: pecaId }));
    if (!pecaId) {
      setPreviewsStep(prev => {
        const next = { ...prev };
        delete next[stepIdx];
        return next;
      });
      return;
    }

    const stepPeca = stepPecas[stepIdx];
    const identidadeDisponivel = hasStepIdentity(stepPeca);
    if (!identidadeDisponivel) {
      setPreviewsStep(prev => ({
        ...prev,
        [stepIdx]: {
          loading: false,
          data: null,
          error: 'Identidade STEP V1 indisponível para consultar a proveniência.',
        },
      }));
      return;
    }

    setPreviewsStep(prev => ({
      ...prev,
      [stepIdx]: { loading: true, data: null, error: null },
    }));
    try {
      const response = await api.post('/api/step/importacao/preview', {
        peca_id: Number(pecaId),
        file_sha256: stepPeca.file_sha256,
        raw_solid_ordinal: stepPeca.raw_solid_ordinal,
        identifier_version: stepPeca.identifier_version,
      });
      setPreviewsStep(prev => ({
        ...prev,
        [stepIdx]: { loading: false, data: response.data, error: null },
      }));
    } catch (error) {
      console.error('Preview de proveniência STEP:', error);
      const detail = error.response?.data?.detail;
      setPreviewsStep(prev => ({
        ...prev,
        [stepIdx]: {
          loading: false,
          data: null,
          error: typeof detail === 'string'
            ? detail
            : 'Não foi possível consultar a proveniência STEP.',
        },
      }));
    }
  };

  const handleSalvarAtribuicoes = async () => {
    if (stepSubmitInFlight.current || stepConcluido) return;
    stepSubmitInFlight.current = true;

    const naoAtribuidas = Object.values(atribuicoes).filter(v => !v).length;
    if (naoAtribuidas > 0) {
      const continuar = await confirm({
        title: 'Peças não atribuídas',
        message: `${naoAtribuidas} peça(s) não foram atribuídas. Deseja continuar mesmo assim?`,
        confirmLabel: 'Continuar',
      });
      if (!continuar) {
        stepSubmitInFlight.current = false;
        return;
      }
    }

    const criarFalhaConfirmacao = (message) => {
      const failure = new Error(message);
      failure.stepConfirmationFailure = true;
      return failure;
    };

    let salvos = 0;
    let confirmados = 0;
    let conflitos = 0;
    setLoadingStep(true);
    setEtapaStep('saving');
    setErroStep(null);
    setSucessoStep(null);
    try {
      for (const [stepIdx, pecaId] of Object.entries(atribuicoes)) {
        if (!pecaId) continue;
        const numericStepIdx = parseInt(stepIdx, 10);
        const stepPeca = stepPecas[numericStepIdx];
        if (previewsStep[numericStepIdx]?.geometrySaved) continue;
        const formData = createStepGeometryFormData(stepPeca);
        setEtapaStep('saving');
        await api.put(`/pecas/${pecaId}/salvar`, formData);
        salvos++;

        const preview = previewsStep[numericStepIdx]?.data;
        setPreviewsStep(prev => ({
          ...prev,
          [numericStepIdx]: {
            ...prev[numericStepIdx],
            geometrySaved: true,
            confirmationStatus: preview?.estado === 'CONFLICT' ? 'conflict' : 'pending',
          },
        }));
        if (!preview) {
          const message = (
            'Geometria salva, mas a proveniência STEP não foi confirmada: preview indisponível.'
          );
          setPreviewsStep(prev => ({
            ...prev,
            [numericStepIdx]: {
              ...prev[numericStepIdx],
              geometrySaved: true,
              confirmationStatus: 'pending',
              confirmationError: message,
            },
          }));
          throw criarFalhaConfirmacao(message);
        }
        if (preview.estado === 'CONFLICT') {
          conflitos++;
          continue;
        }
        if (preview.estado !== 'NEW' && preview.estado !== 'NO_OP') {
          const message = (
            'Geometria salva, mas a proveniência STEP não foi confirmada: estado de preview inválido.'
          );
          setPreviewsStep(prev => ({
            ...prev,
            [numericStepIdx]: {
              ...prev[numericStepIdx],
              geometrySaved: true,
              confirmationStatus: 'pending',
              confirmationError: message,
            },
          }));
          throw criarFalhaConfirmacao(message);
        }

        const arquivoOriginal = localizarArquivoOriginal(stepFile, stepPeca.arquivo_origem);
        if (!arquivoOriginal) {
          const message = (
            'Geometria salva, mas a proveniência STEP não foi confirmada: arquivo original indisponível ou ambíguo.'
          );
          setPreviewsStep(prev => ({
            ...prev,
            [numericStepIdx]: {
              ...prev[numericStepIdx],
              geometrySaved: true,
              confirmationStatus: 'pending',
              confirmationError: message,
            },
          }));
          throw criarFalhaConfirmacao(message);
        }

        setEtapaStep('confirming');
        setPreviewsStep(prev => ({
          ...prev,
          [numericStepIdx]: {
            ...prev[numericStepIdx],
            geometrySaved: true,
            confirmationStatus: 'confirming',
            confirmationError: null,
          },
        }));
        try {
          const response = await confirmarProvenienciaStep({
            file: arquivoOriginal,
            pecaId,
            stepPeca,
          });
          confirmados++;
          setPreviewsStep(prev => ({
            ...prev,
            [numericStepIdx]: {
              ...prev[numericStepIdx],
              loading: false,
              data: response.data,
              error: null,
              geometrySaved: true,
              confirmationStatus: 'confirmed',
              confirmed: true,
              confirmationError: null,
            },
          }));
        } catch (error) {
          console.error('Confirmação de proveniência STEP:', error);
          const failure = interpretarFalhaConfirmacao(error, pecaId);
          setPreviewsStep(prev => ({
            ...prev,
            [numericStepIdx]: {
              ...prev[numericStepIdx],
              loading: false,
              data: failure.data || prev[numericStepIdx]?.data,
              error: null,
              geometrySaved: true,
              confirmationStatus: failure.confirmationStatus,
              confirmed: false,
              confirmationError: failure.message,
            },
          }));
          throw criarFalhaConfirmacao(failure.message);
        }
      }

      const resumoConflitos = conflitos > 0
        ? ` ${conflitos} conflito(s) permaneceram sem confirmação.`
        : '';
      setSucessoStep(
        `${salvos} peça(s) atualizada(s); ${confirmados} proveniência(s) confirmada(s).${resumoConflitos}`
      );
      setStepConcluido(true);
      alert(`${salvos} peça(s) atualizada(s) com sucesso!`);
      onPartsUpdated();
    } catch (error) {
      if (error.stepConfirmationFailure) {
        setErroStep(error.message);
        if (salvos > 0) onPartsUpdated();
      } else {
        const errorMsg = error.response?.data?.detail;
        setErroStep(
          typeof errorMsg === 'object'
            ? JSON.stringify(errorMsg)
            : errorMsg || 'Erro ao salvar atribuições'
        );
      }
    } finally {
      setLoadingStep(false);
      setEtapaStep(null);
      stepSubmitInFlight.current = false;
    }
  };

  const handleRepetirConfirmacao = async (stepIdx) => {
    const preview = previewsStep[stepIdx];
    if (
      !preview?.geometrySaved
      || preview.confirmationStatus !== 'pending'
      || confirmationRetriesInFlight.current.has(stepIdx)
    ) return;

    const pecaId = atribuicoes[stepIdx];
    const stepPeca = stepPecas[stepIdx];
    const arquivoOriginal = localizarArquivoOriginal(stepFile, stepPeca?.arquivo_origem);
    if (!pecaId || !stepPeca || !arquivoOriginal) {
      const message = (
        'A confirmação não pode ser repetida: arquivo original indisponível ou ambíguo. '
        + 'A geometria já salva foi preservada.'
      );
      setPreviewsStep(prev => ({
        ...prev,
        [stepIdx]: {
          ...prev[stepIdx],
          geometrySaved: true,
          confirmationStatus: 'pending',
          confirmationError: message,
        },
      }));
      setErroStep(message);
      return;
    }

    confirmationRetriesInFlight.current.add(stepIdx);
    setPreviewsStep(prev => ({
      ...prev,
      [stepIdx]: {
        ...prev[stepIdx],
        geometrySaved: true,
        confirmationStatus: 'confirming',
        confirmationError: null,
      },
    }));
    setErroStep(null);

    try {
      const response = await confirmarProvenienciaStep({
        file: arquivoOriginal,
        pecaId,
        stepPeca,
      });
      setPreviewsStep(prev => ({
        ...prev,
        [stepIdx]: {
          ...prev[stepIdx],
          loading: false,
          data: response.data,
          error: null,
          geometrySaved: true,
          confirmationStatus: 'confirmed',
          confirmed: true,
          confirmationError: null,
        },
      }));
      setSucessoStep('Geometria já salva; proveniência STEP confirmada.');
    } catch (error) {
      console.error('Repetição da confirmação de proveniência STEP:', error);
      const failure = interpretarFalhaConfirmacao(error, pecaId);
      setPreviewsStep(prev => ({
        ...prev,
        [stepIdx]: {
          ...prev[stepIdx],
          loading: false,
          data: failure.data || prev[stepIdx]?.data,
          error: null,
          geometrySaved: true,
          confirmationStatus: failure.confirmationStatus,
          confirmed: false,
          confirmationError: failure.message,
        },
      }));
      setErroStep(failure.message);
    } finally {
      confirmationRetriesInFlight.current.delete(stepIdx);
    }
  };

  const fecharModal = () => {
    setShowModalStep(false);
    setStepFile(null);
    setStepPecas([]);
    setAtribuicoes({});
    setPreviewsStep({});
    setErroStep(null);
    setSucessoStep(null);
    setEtapaStep(null);
    setStepConcluido(false);
    stepSubmitInFlight.current = false;
    confirmationRetriesInFlight.current.clear();
  };

  const handleGerarTodosPDFs = async () => {
    const pecasComFuros = pecas.filter(peca => {
      const furosVerticais = peca.furos?.verticais?.length || 0;
      const furosHorizontais = peca.furos?.horizontais?.length || 0;
      return (furosVerticais + furosHorizontais) > 0;
    });
    if (pecasComFuros.length === 0) { alert('Nenhuma peça com furos para gerar PDF!'); return; }
    setConfirmPDF(pecasComFuros);
  };

  const confirmarEditarAntes = () => {
    setPecasParaEditar(confirmPDF);
    setShowEditorPDF(true);
    setConfirmPDF(null);
  };

  const confirmarGerarDireto = () => {
    setPendingPecas(confirmPDF);
    setPendingConfig({});
    setShowModalDownload(true);
    setConfirmPDF(null);
  };

  const gerarPDFsDireto = async (pecasParaGerar, configPecas) => {
    setGerandoPDFs(true);
    setShowModalDownload(false);
    try {
      const pecasComConfig = pecasParaGerar.map(peca => ({
        id: peca.id,
        bordas: configPecas[peca.id]?.bordas || peca.bordas || {},
        cotasOffset: configPecas[peca.id]?.cotasOffset || {},
        transformacao: configPecas[peca.id]?.transformacao ?? null
      }));
      const response = await api.post(
        '/editor/generate-pdfs-batch',
        { peca_ids: pecasParaGerar.map(p => p.id), status: statusProduto, pecas_config: pecasComConfig },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'documentos_furacao.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      const totalFalhas = parseInt(response.headers['x-total-falhas'] || '0', 10);
      const totalAvisosFuros = parseInt(response.headers['x-total-avisos-furos'] || '0', 10);
      if (totalFalhas > 0) {
        console.error(`Lote de PDFs: ${totalFalhas} peça(s) falharam — ver _relatorio.json dentro do ZIP.`);
        alert(
          `PDFs gerados com ${totalFalhas} falha(s)` +
          (totalAvisosFuros > 0 ? ` e ${totalAvisosFuros} aviso(s) de furo` : '') +
          `. Confira o arquivo _relatorio.json dentro do ZIP baixado para ver quais peças ` +
          `falharam e por quê.`
        );
      } else if (totalAvisosFuros > 0) {
        console.warn(`Lote de PDFs: ${totalAvisosFuros} aviso(s) de furo — ver "avisos_furos" no _relatorio.json dentro do ZIP.`);
        alert(
          `PDFs gerados com ${totalAvisosFuros} aviso(s) de furo (furos que não puderam ` +
          `ser transformados e ficaram na posição original). Confira "avisos_furos" no ` +
          `_relatorio.json dentro do ZIP baixado.`
        );
      } else {
        alert('PDFs gerados com sucesso!');
      }
    } catch (error) {
      let mensagem = error.message;
      if (error.response?.data instanceof Blob) {
        try {
          const texto = await error.response.data.text();
          mensagem = JSON.parse(texto).detail || mensagem;
        } catch (parseErr) {
          console.error('Erro ao interpretar resposta de erro do lote de PDFs:', parseErr);
        }
      } else if (error.response?.data?.detail) {
        mensagem = error.response.data.detail;
      }
      alert('Erro ao gerar PDFs: ' + mensagem);
    } finally {
      setGerandoPDFs(false);
    }
  };

  const handleEditorPDFGenerate = (configPecas) => {
    setShowEditorPDF(false);
    setPendingPecas(pecasParaEditar);
    setPendingConfig(configPecas);
    setShowModalDownload(true);
  };

  const handleGerarTodosMPRs = async () => {
    const pecasComFuros = pecas.filter(peca => {
      const furosVerticais = peca.furos?.verticais?.length || 0;
      const furosHorizontais = peca.furos?.horizontais?.length || 0;
      return (furosVerticais + furosHorizontais) > 0;
    });
    if (pecasComFuros.length === 0) { alert('Nenhuma peça com furos para gerar MPR!'); return; }
    if (!await confirm({
      title: 'Gerar MPRs',
      message: `Gerar MPR de ${pecasComFuros.length} peça(s) com furação?\n\nPeças: ${pecasComFuros.map(p => p.nome).join(', ')}`,
      confirmLabel: 'Gerar',
    })) return;
    setGerandoMPRs(true);
    try {
      const response = await api.post(
        '/editor/generate-mprs-batch',
        { peca_ids: pecasComFuros.map(p => p.id) }
      );
      const { zip_base64, zip_filename, gerados, falhas } = response.data;

      const byteChars = atob(zip_base64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/zip' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = zip_filename || `MPRs_${codigoProduto}_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (falhas && falhas.length > 0) {
        console.error('Falha ao gerar MPR de peça(s):', falhas);
        alert(
          `${gerados.length} MPR(s) gerado(s). ${falhas.length} peça(s) falharam:\n` +
          falhas.map(f => `- peça ${f.peca_id}: ${f.motivo}`).join('\n')
        );
      } else {
        alert(`${gerados.length} MPR(s) gerado(s) com sucesso!`);
      }
    } catch (error) {
      console.error('Erro ao gerar MPRs em lote:', error);
      alert('Erro ao gerar MPRs: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGerandoMPRs(false);
    }
  };


  const renderPreviewStep = (stepIdx) => {
    const preview = previewsStep[stepIdx];
    if (!atribuicoes[stepIdx]) {
      return <span className="step-preview-placeholder">Selecione uma peça</span>;
    }
    if (preview?.loading) {
      return <span className="step-preview-loading" role="status">Consultando...</span>;
    }
    if (preview?.error) {
      return <span className="step-preview-error" role="alert">{preview.error}</span>;
    }
    if (!preview?.data) {
      return <span className="step-preview-placeholder">Preview indisponível</span>;
    }

    const { estado, peca_associada_id: pecaAssociadaId } = preview.data;
    const confirmationText = (() => {
      if (!preview.geometrySaved) return null;
      if (preview.confirmationStatus === 'confirmed') {
        return 'Geometria salva. Proveniência confirmada.';
      }
      if (preview.confirmationStatus === 'confirming') {
        return 'Geometria salva. Confirmação de proveniência em andamento...';
      }
      if (preview.confirmationStatus === 'pending') {
        return 'Geometria salva. Confirmação de proveniência pendente.';
      }
      return 'Geometria salva.';
    })();
    const retryAction = preview.geometrySaved
      && preview.confirmationStatus === 'pending'
      && (estado === 'NEW' || estado === 'NO_OP')
      ? (
        <button
          type="button"
          className="btn-secundario"
          onClick={() => handleRepetirConfirmacao(stepIdx)}
        >
          Repetir confirmação
        </button>
      )
      : null;

    if (estado === 'NEW') {
      return (
        <div className="step-preview-result">
          <Badge variant="info" dot>NEW</Badge>
          <span role={preview.confirmationStatus === 'confirming' ? 'status' : undefined}>
            {confirmationText || 'Origem ainda não associada.'}
          </span>
          {preview.confirmationError && (
            <span className="step-preview-error">{preview.confirmationError}</span>
          )}
          {retryAction}
        </div>
      );
    }
    if (estado === 'NO_OP') {
      return (
        <div className="step-preview-result">
          <Badge variant="success" dot>NO_OP</Badge>
          <span role={preview.confirmationStatus === 'confirming' ? 'status' : undefined}>
            {confirmationText || 'Já associada a esta peça.'}
          </span>
          {preview.confirmationError && (
            <span className="step-preview-error">{preview.confirmationError}</span>
          )}
          {retryAction}
        </div>
      );
    }
    if (estado === 'CONFLICT') {
      return (
        <div className="step-preview-result">
          <Badge variant="warning" dot>CONFLICT</Badge>
          <span>
            {preview.geometrySaved ? 'Geometria salva. ' : ''}
            Associada à peça
            {pecaAssociadaId ? ` #${pecaAssociadaId}` : ' informada pelo servidor'}.
          </span>
          {preview.confirmationError && (
            <span className="step-preview-error">{preview.confirmationError}</span>
          )}
        </div>
      );
    }
    return <span className="step-preview-error" role="alert">Estado de preview desconhecido.</span>;
  };

  const statusOptions = (
    <>
      <option value="CÓPIA CONTROLADA">CÓPIA CONTROLADA</option>
      <option value="EM REVISÃO">EM REVISÃO</option>
      <option value="CÓPIA ÚNICA">CÓPIA ÚNICA</option>
    </>
  );

  return (
    <>
      <div className="cw-product-action-bar">
        <Button variant="secondary" onClick={() => setShowModalStep(true)}>Importar STEP</Button>
        <Button variant="secondary" onClick={handleGerarTodosMPRs} loading={gerandoMPRs}>Gerar MPRs</Button>
        <Button variant="secondary" onClick={handleGerarTodosPDFs} loading={gerandoPDFs}>Gerar PDFs</Button>
        <div className="cw-product-action-status">
          <label htmlFor="product-status">Status do PDF:</label>
          <Select id="product-status" value={statusProduto} onChange={(e) => setStatusProduto(e.target.value)}>
            {statusOptions}
          </Select>
        </div>
        {Number.isInteger(productId) && productId > 0 && (
          <Button variant="secondary" onClick={() => navigate(`/roteiro?product_id=${productId}`)}>
            Novo Roteiro
          </Button>
        )}
      </div>
      {/* ===== MODAL IMPORTAR STEP ===== */}
      {showModalStep && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal-step" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Importar STEP → MPR</h2>
              <button className="btn-fechar" onClick={fecharModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="step-upload">
                <label className="upload-area">
                  <input
                    type="file"
                    accept=".step,.stp,.STEP,.STP"
                    onChange={handleStepFileChange}
                    disabled={loadingStep}
                    multiple
                  />
                  {stepFile && stepFile.length > 0 ? (
                    <div className="file-info">
                      <span className="file-icon"></span>
                      <span className="file-name">{stepFile.length} arquivo(s) selecionado(s)</span>
                      <span className="file-size">({stepFile.map(f => f.name).join(', ')})</span>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <span className="upload-icon"></span>
                      <span>Clique ou arraste arquivos .STEP (múltiplos)</span>
                    </div>
                  )}
                </label>
              </div>
              {loadingStep && (
                <div className="loading-step" role="status">
                  {etapaStep === 'saving' && 'Salvando geometria...'}
                  {etapaStep === 'confirming' && 'Confirmando proveniência...'}
                  {etapaStep === 'parsing' && 'Processando arquivo STEP...'}
                </div>
              )}
              {erroStep && <div className="alerta erro" role="alert">{erroStep}</div>}
              {sucessoStep && (
                <div className="form-alerta sucesso" role="status">{sucessoStep}</div>
              )}
              {stepPecas.length > 0 && (
                <div className="step-pecas-lista">
                  <h3>{stepPecas.length} peça(s) encontrada(s) no STEP</h3>
                  <table className="tabela-atribuicao">
                    <thead>
                      <tr>
                        <th>Peça no STEP</th>
                        <th>Dimensões</th>
                        <th>Furos</th>
                        <th>Atribuir a</th>
                        <th>Proveniência</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stepPecas.map((stepPeca, idx) => (
                        <tr key={idx}>
                          <td className="step-peca-nome">
                            {stepPeca.nome || stepPeca.arquivo_origem?.replace(/\.(step|stp)$/i, '')}
                          </td>
                          <td className="step-peca-dims">
                            {stepPeca.comprimento} × {stepPeca.largura} × {stepPeca.espessura}mm
                          </td>
                          <td className="step-peca-furos">{stepPeca.furos?.length || 0}</td>
                          <td>
                            <select
                              value={atribuicoes[idx] || ''}
                              onChange={(e) => handleAtribuicaoChange(idx, e.target.value)}
                              disabled={
                                loadingStep
                                || stepConcluido
                                || previewsStep[idx]?.loading
                                || previewsStep[idx]?.geometrySaved
                                || previewsStep[idx]?.confirmationStatus === 'confirming'
                              }
                              className="select-atribuicao"
                            >
                              <option value="">-- Selecionar --</option>
                              {pecas.map((peca) => (
                                <option key={peca.id} value={peca.id}>
                                  {peca.codigo} - {peca.nome}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="step-preview-cell" aria-live="polite">
                            {renderPreviewStep(idx)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {stepPecas.length > 0 && (
              <div className="modal-footer">
                <button
                  className="btn-primario"
                  onClick={handleSalvarAtribuicoes}
                  disabled={
                    loadingStep
                    || stepConcluido
                    || Object.values(previewsStep).some(preview => (
                      preview.loading
                      || preview.confirmationStatus === 'pending'
                      || preview.confirmationStatus === 'confirming'
                    ))
                  }
                >
                  {etapaStep === 'saving' && 'Salvando...'}
                  {etapaStep === 'confirming' && 'Confirmando proveniência...'}
                  {!etapaStep && (stepConcluido ? 'Concluído' : 'Atribuir e Salvar')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {confirmEl}
      <ConfirmDialog
        open={confirmPDF !== null}
        title="Gerar PDFs de furação"
        message={confirmPDF ? `Encontrada(s) ${confirmPDF.length} peça(s) com furação.\n\nDeseja editar os PDFs antes de gerar?` : ''}
        confirmLabel="Editar antes"
        cancelLabel="Gerar direto"
        onConfirm={confirmarEditarAntes}
        onCancel={confirmarGerarDireto}
        onClose={() => setConfirmPDF(null)}
      />

      {showEditorPDF && (
        <EditorPDF
          pecas={pecasParaEditar}
          onClose={() => setShowEditorPDF(false)}
          onGenerate={handleEditorPDFGenerate}
        />
      )}

      {showModalDownload && (
        <div className="modal-overlay">
          <div className="modal-download-pdf">
            <h3>Baixar PDFs</h3>
            <p className="modal-download-info">
              {pendingPecas.length} peça(s) selecionada(s).{' '}
              Os arquivos serão salvos na pasta de Downloads do navegador.
            </p>

            <div className="modal-download-acoes">
              <button
                className="btn-cancelar"
                onClick={() => setShowModalDownload(false)}
              >
                Cancelar
              </button>
              <button
                className="btn-baixar"
                disabled={gerandoPDFs}
                onClick={() => gerarPDFsDireto(pendingPecas, pendingConfig)}
              >
                {gerandoPDFs ? 'Gerando...' : 'Baixar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProductOperations;
